import {
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
} from '@chakra-ui/react'

import './WithdrawApplicationModal.scss'
import ApplyV2Textarea from '@core/components/ApplyDialogV2/ApplyV2Textarea'
import ApplyV2Tooltip from '@core/components/ApplyDialogV2/ApplyV2Tooltip'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'

import React from 'react'

const baseClass = 'WithdrawApplicationModal'

const REASON_MIN_LENGTH = 5

function WithdrawApplicationModal({
    isOpen,
    onClose,
    onWithdraw,
    isLoading,
    reasonError,
    generalError,
    onResetErrors,
}: {
    isOpen: boolean
    onClose: () => void
    onWithdraw: (reason: string) => void
    isLoading: boolean
    reasonError?: string
    generalError?: string
    onResetErrors?: () => void
}) {
    const { getTitle } = useApplyV2NavTranslations()
    const [reason, setReason] = React.useState<string>('')
    const [isReasonTouched, setIsReasonTouched] = React.useState(false)

    React.useEffect(() => {
        if (isOpen) {
            setReason('')
            setIsReasonTouched(false)
            onResetErrors?.()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen])

    const trimmedReason = reason.trim()
    const clientReasonError = !trimmedReason
        ? getTitle('3924', 'Required field')
        : trimmedReason.length < REASON_MIN_LENGTH
          ? getTitle('11031', 'Enter at least {{count}} characters', { count: REASON_MIN_LENGTH })
          : ''
    const reasonErrorMessage = (isReasonTouched && clientReasonError) || reasonError || ''

    return (
        <Modal isCentered size={'lg'} isOpen={isOpen} onClose={onClose} motionPreset="slideInBottom">
            <ModalOverlay className={`${baseClass}__overlay`} />
            <ModalContent
                className={`${baseClass}__content`}
                containerProps={{
                    padding: '16px',
                }}
                maxWidth={'396px'}
            >
                <ModalHeader className={`${baseClass}__header`} px={'20px'} pt={'20px'}>
                    <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M0 22C0 9.84974 9.84974 0 22 0C34.1503 0 44 9.84974 44 22C44 34.1503 34.1503 44 22 44C9.84974 44 0 34.1503 0 22Z"
                            fill="#FCEAEA"
                        />
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M12.9141 21.9974C12.9141 16.9808 16.9808 12.9141 21.9974 12.9141C27.014 12.9141 31.0807 16.9808 31.0807 21.9974C31.0807 27.014 27.014 31.0807 21.9974 31.0807C16.9808 31.0807 12.9141 27.014 12.9141 21.9974ZM21.9974 23.5807C21.5832 23.5807 21.2474 23.2449 21.2474 22.8307V17.8307C21.2474 17.4165 21.5832 17.0807 21.9974 17.0807C22.4116 17.0807 22.7474 17.4165 22.7474 17.8307V22.8307C22.7474 23.2449 22.4116 23.5807 21.9974 23.5807ZM21.9974 26.1641C22.4577 26.1641 22.8307 25.791 22.8307 25.3307C22.8307 24.8705 22.4577 24.4974 21.9974 24.4974C21.5372 24.4974 21.1641 24.8705 21.1641 25.3307C21.1641 25.791 21.5372 26.1641 21.9974 26.1641Z"
                            fill="#B62626"
                        />
                    </svg>

                    <ApplyV2Tooltip
                        label={getTitle('181', 'Close')}
                        placement="top"
                        style={{
                            position: 'absolute',
                            right: '20px',
                            top: '20px',
                            // width: '32px',
                        }}
                    >
                        <ModalCloseButton
                            className={`${baseClass}__close-button`}
                            color={'#77777C'}
                            borderRadius={9999}
                            backgroundColor={'#F5F5F5'}
                            position={'relative'}
                            _hover={{
                                bg: '#e3e3e3',
                            }}
                            top={{
                                base: '0',
                                // sm: '30px',
                            }}
                            right={{
                                base: '0',
                                // sm: '40px',
                            }}
                        />
                    </ApplyV2Tooltip>
                    {/*</Tooltip>*/}
                </ModalHeader>
                <ModalBody
                    className={`${baseClass}__body`}
                    // px={{
                    //     base: '20px',
                    //     sm: '48px',
                    // }}
                    // pb={'0'}
                    px={'20px'}
                    py={'0px'}
                >
                    <Text mb={'8px'} fontSize={'16px'} fontWeight={600} color={'#0C0C0D'}>
                        {getTitle('13133', 'Withdraw application?')}
                    </Text>
                    <Text mb={'13px'} fontSize={'13px'} lineHeight={'18px'} fontWeight={500} color={'#4B4B4E'}>
                        {getTitle('13134', 'Are you sure you want to withdraw your application for this position?')}
                    </Text>
                    <div className={`${baseClass}__info`}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="8" cy="8" r="7" fill="white" />
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M0.734375 8.00104C0.734375 3.98777 3.98777 0.734375 8.00104 0.734375C12.0143 0.734375 15.2677 3.98777 15.2677 8.00104C15.2677 12.0143 12.0143 15.2677 8.00104 15.2677C3.98777 15.2677 0.734375 12.0143 0.734375 8.00104ZM8.00106 6.73437C8.33243 6.73437 8.60106 7.003 8.60106 7.33437V11.3344C8.60106 11.6657 8.33243 11.9344 8.00106 11.9344C7.66969 11.9344 7.40106 11.6657 7.40106 11.3344V7.33437C7.40106 7.003 7.66969 6.73437 8.00106 6.73437ZM8.00106 6.00104C8.36925 6.00104 8.66772 5.70256 8.66772 5.33437C8.66772 4.96618 8.36925 4.6677 8.00106 4.6677C7.63287 4.6677 7.33439 4.96618 7.33439 5.33437C7.33439 5.70256 7.63287 6.00104 8.00106 6.00104Z"
                                fill="#77777C"
                            />
                        </svg>
                        <Text fontSize={'12px'} lineHeight={'16px'} fontWeight={500} color={'#4B4B4E'}>
                            {getTitle(
                                '13135',
                                'Your application will remain visible to recruiters (but marked as “Withdrawn”)'
                            )}
                        </Text>
                    </div>
                    <ApplyV2Textarea
                        value={reason}
                        // placeholder={getTitle('2371', 'Reason')}
                        onChange={(e) => {
                            setReason(e)
                            onResetErrors?.()
                        }}
                        onBlur={() => setIsReasonTouched(true)}
                        errorMessage={reasonErrorMessage}
                        isDisabled={isLoading}
                    />
                    {generalError && (
                        <div className={`${baseClass}__error`} role="alert">
                            {generalError}
                        </div>
                    )}
                </ModalBody>
                <ModalFooter
                    className={`${baseClass}__footer`}
                    // px={{
                    //     base: '20px',
                    //     sm: '48px',
                    // }}
                    // pb={{
                    //     base: '20px',
                    //     sm: '44px',
                    // }}
                    // pt={{
                    //     base: '20px',
                    //     sm: '40px',
                    // }}
                    p={'20px'}
                    display={'flex'}
                    gap={'8px'}
                    alignItems={'center'}
                    justifyContent={'end'}
                    flexDirection={{
                        base: 'column-reverse',
                        sm: 'row',
                    }}
                >
                    <button
                        className={`${baseClass}__footer--btn ${baseClass}__footer--btn-cancel`}
                        type={'button'}
                        onClick={onClose}
                    >
                        {getTitle('416', 'Cancel')}
                    </button>

                    <button
                        className={`${baseClass}__footer--btn ${baseClass}__footer--btn-confirm`}
                        type={'button'}
                        disabled={isLoading}
                        aria-busy={isLoading}
                        onClick={() => {
                            if (!clientReasonError) {
                                onWithdraw(trimmedReason)
                            } else {
                                setIsReasonTouched(true)
                            }
                        }}
                        id={'ra-button-job-post-action-application-form-close-ok'}
                    >
                        {getTitle('10180', 'Withdraw')}
                    </button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    )
}

export default WithdrawApplicationModal
